import { VisuallyHidden } from '@react-aria/visually-hidden';
import { HTMLAttributes, PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { getFocusRingBorderCSS } from '@ardoq/style-helpers';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { trackEvent } from 'tracking/tracking';
import { text2Bold } from '@ardoq/typography';
import { colors } from '@ardoq/design-tokens';
import { DisabledZoomControls } from '@ardoq/view-settings';
import { t, type I18nLocale, DEFAULT_ARDOQ_I18NLOCALE } from '@ardoq/i18n';

const ZOOM_ICON_HEIGHT = '36px';

export const ZoomButtonIcon = styled(Icon).attrs({
  style: { height: ZOOM_ICON_HEIGHT },
})`
  margin-top: auto;
  && {
    line-height: ${ZOOM_ICON_HEIGHT};
  }
`;
const activeZoomButtonStyle = css`
  background-color: ${colors.grey200};
  color: ${colors.grey900};
`;
export const ZoomButton = styled.button<{
  $disabled?: boolean;
  $isActive?: boolean;
}>`
  ${text2Bold}
  ${({ $isActive }) =>
    $isActive
      ? activeZoomButtonStyle
      : css`
          color: color-mix(in srgb, ${colors.grey900} 70%, transparent);
          background-color: transparent;
        `}
  width: 100%;
  height: ${ZOOM_ICON_HEIGHT};
  margin-bottom: 5px;
  box-shadow: none;
  border: none;
  font-weight: 400;
  letter-spacing: 0.5px;
  line-height: ${ZOOM_ICON_HEIGHT};
  min-width: 64px;
  padding: 0 8px;
  transition: all 0.2s ease-out;
  text-align: center;

  ${({ $disabled }) =>
    $disabled
      ? css`
          opacity: 0.32;
        `
      : css`
          cursor: pointer;

          &:hover,
          &.active,
          &:active,
          &:focus {
            ${activeZoomButtonStyle}
          }
          &:focus-visible {
            ${getFocusRingBorderCSS()}
          }
        `}
`;

type ZoomControlProps = {
  iconName: IconName;
  disabled?: boolean;
  isActive?: boolean;
  label: string;
} & Omit<HTMLAttributes<HTMLButtonElement>, 'style'>;

export const ZoomControl = ({
  iconName,
  onClick,
  disabled,
  isActive,
  label,
  ...labelProps
}: ZoomControlProps) => (
  <ZoomButton
    onClick={e => {
      e.stopPropagation();
      if (!disabled) {
        trackEvent('Clicked zoom control', { iconName });
        return onClick?.(e);
      }
    }}
    data-tooltip-text={label}
    disabled={disabled}
    $disabled={disabled}
    $isActive={isActive}
    {...labelProps}
  >
    <ZoomButtonIcon iconName={iconName} iconSize={IconSize.LARGE} />
    <VisuallyHidden>{label}</VisuallyHidden>
  </ZoomButton>
);

type ZoomControlsProps = {
  zoomIn: () => void;
  zoomCenter: () => void;
  zoomOut: () => void;
  disabledState?: DisabledZoomControls;
  i18nLocale?: I18nLocale;
};

const ZoomControls = ({
  zoomIn,
  zoomCenter,
  zoomOut,
  children,
  disabledState = DisabledZoomControls.NONE,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: PropsWithChildren<ZoomControlsProps>) => (
  <>
    <ZoomControl
      iconName={IconName.ZOOM_OUT_MAP}
      onClick={zoomCenter}
      label={t(
        i18nLocale,
        'atomicComponents:zoomControls.fitWindow',
        'Fit window'
      )}
      disabled={Boolean(disabledState & DisabledZoomControls.FIT_WINDOW)}
    />
    <ZoomControl
      iconName={IconName.ZOOM_IN}
      onClick={zoomIn}
      label={t(i18nLocale, 'atomicComponents:zoomControls.zoomIn', 'Zoom in')}
      disabled={Boolean(disabledState & DisabledZoomControls.ZOOM_IN)}
    />
    <ZoomControl
      iconName={IconName.ZOOM_OUT}
      onClick={zoomOut}
      label={t(i18nLocale, 'atomicComponents:zoomControls.zoomOut', 'Zoom out')}
      disabled={Boolean(disabledState & DisabledZoomControls.ZOOM_OUT)}
    />
    {children}
  </>
);

export { ZoomControls };
